<template>
  <Card v-bind="$attrs" class="c-image-list-item cursor-pointer flex-1" @click="actionCallback">
    <template #header>
      <div class="c-image-list-item-media relative flex">
        <Image :src="`${fullImageUrl}`" class="w-full h-full custom-image" v-if="src" />
        <Avatar :label="initial" class="w-full h-full custom-avatar" v-else />
        <div class="c-image-list-item-overlay absolute bottom-0 left-0 right-0">
          <slot name="imageOverlay" />
        </div>
      </div>
    </template>

    <template #content>
      <div class="flex flex-col">
        <div class="flex mb-2">
          <h2 class="c-image-list-item-title font-bold">{{ title }}</h2>
        </div>
        <p v-if="subtitle" class="text-[var(--color-text-secondary)] m-0">{{ subtitle }}</p>
        <div class="flex flex-wrap" style="gap: 0.5rem">
          <slot name="tags" />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { Image, Card, Avatar } from '../../../core';
import { computed, type ComputedRef } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
  },
  staticImage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['action']);
const src = defineModel('srcImg', {
  type: String,
});

// Get title initials
const initial = computed(() => {
  return props.title?.match(/(\b\S)?/g).join('');
});

const fullImageUrl: ComputedRef<string> = computed(() => {
  if (props.staticImage) return src.value;
  return import.meta.env.VITE_AWS_BUCKET + src.value;
});

const actionCallback = () => {
  emit('action');
};
</script>

<script lang="ts">
export default { name: 'ImageListItem' };
</script>

<style scoped lang="scss">
.c-image-list-item {
  overflow: hidden;
  background-color: var(--color-surface-raised);
  border: var(--border-width-default) solid var(--color-border-default);
  border-radius: var(--radius-role-md);
  box-shadow: none;

  &:hover {
    border-color: var(--color-border-strong);
  }

  // Outranks PrimaryRecordLayout's descendant .p-card-body rules, which reach nested cards.
  &.p-card.p-component > :deep(.p-card-body) {
    padding: var(--space-16) var(--space-16) var(--space-24);
  }

  :deep(.p-card-content) {
    padding: 0;
  }
}

.c-image-list-item-overlay {
  padding: var(--space-16);
}

.c-image-list-item-media {
  aspect-ratio: 2 / 1;
  background-color: var(--color-surface-sunken);
}

.c-image-list-item-title {
  margin: 0;
  font-size: var(--typography-heading-md-font-size);
  line-height: var(--typography-heading-md-line-height);
  color: var(--color-text-primary);
}

.custom-image {
  object-fit: cover;
}

:deep(.custom-avatar) {
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-role-none);

  .p-avatar-text {
    font-family: var(--typography-display-font-family);
    font-size: var(--typography-display-font-size);
    font-weight: var(--typography-display-font-weight);
    line-height: var(--typography-display-line-height);
    letter-spacing: var(--typography-display-letter-spacing);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
  }
}
</style>
